import api from './api'

export const ORDER_WEBHOOK_EVENT_OPTIONS = [
  {
    value: 'order.created',
    label: 'Pedido creado',
    description: 'Cuando se registra un pedido nuevo desde el asistente, la vitrina o la carga manual.',
  },
  {
    value: 'order.status_changed',
    label: 'Cambio de estado',
    description: 'Cuando el pedido pasa a confirmado, en proceso, listo o completado.',
  },
  {
    value: 'order.cancelled',
    label: 'Pedido cancelado',
    description: 'Cuando se cancela un pedido, con el motivo si el equipo lo escribió.',
  },
  {
    value: 'order.updated',
    label: 'Pedido actualizado',
    description: 'Cuando el equipo corrige datos, productos o cantidades del pedido.',
  },
] as const

export type OrderWebhookEventName = (typeof ORDER_WEBHOOK_EVENT_OPTIONS)[number]['value']

export type AppointmentWebhookEventName =
  | 'appointment.requested'
  | 'appointment.confirmed'
  | 'appointment.rescheduled'
  | 'appointment.cancelled'
  | 'appointment.expired'
  | 'appointment.no_show'

export type SubscribableWebhookEventName = OrderWebhookEventName | AppointmentWebhookEventName

export const DEFAULT_APPOINTMENT_WEBHOOK_EVENTS: AppointmentWebhookEventName[] = [
  'appointment.requested',
  'appointment.confirmed',
  'appointment.rescheduled',
  'appointment.cancelled',
]

export interface WebhookEventOptionItem<T extends string = SubscribableWebhookEventName> {
  value: T
  label: string
  description: string
}

export interface VerticalWebhookCopy {
  isAppointmentVertical: boolean
  pageTitle: string
  singularLabel: string
  pluralLabel: string
}

export function getVerticalAppointmentWebhookCopy(slug?: string | null): VerticalWebhookCopy {
  switch (slug) {
    case 'salud':
    case 'servicios':
      return {
        isAppointmentVertical: true,
        pageTitle: 'Avisos de citas',
        singularLabel: 'Cita',
        pluralLabel: 'citas',
      }
    case 'belleza_estetica':
      return {
        isAppointmentVertical: true,
        pageTitle: 'Avisos de citas y turnos',
        singularLabel: 'Turno',
        pluralLabel: 'citas y turnos',
      }
    case 'inmobiliaria':
      return {
        isAppointmentVertical: true,
        pageTitle: 'Avisos de visitas',
        singularLabel: 'Visita',
        pluralLabel: 'visitas',
      }
    case 'turismo_posadas':
      return {
        isAppointmentVertical: true,
        pageTitle: 'Avisos de reservas',
        singularLabel: 'Reserva',
        pluralLabel: 'reservas',
      }
    default:
      return {
        isAppointmentVertical: false,
        pageTitle: 'Avisos de pedidos',
        singularLabel: 'Cita',
        pluralLabel: 'citas',
      }
  }
}

export function getAppointmentWebhookEventOptions(
  singularLabel = 'Cita'
): ReadonlyArray<WebhookEventOptionItem<AppointmentWebhookEventName>> {
  const cleanSingular = singularLabel.trim() || 'Cita'
  const lowerSingular = cleanSingular.toLowerCase()
  const isMasculine = lowerSingular === 'turno'
  const adj = (fem: string, masc: string) => (isMasculine ? masc : fem)

  return [
    {
      value: 'appointment.requested',
      label: `${cleanSingular} ${adj('solicitada', 'solicitado')}`,
      description: `Cuando el cliente pide fecha u horario en WhatsApp (solicitud pendiente, aún sin confirmar).`,
    },
    {
      value: 'appointment.confirmed',
      label: `${cleanSingular} ${adj('confirmada', 'confirmado')}`,
      description: `Cuando tu equipo confirma día, hora y responsable. Usa este aviso para bloquear la agenda.`,
    },
    {
      value: 'appointment.rescheduled',
      label: `${cleanSingular} ${adj('reprogramada', 'reprogramado')}`,
      description: `Cuando el equipo reprograma hacia una nueva solicitud vinculada.`,
    },
    {
      value: 'appointment.cancelled',
      label: `${cleanSingular} ${adj('cancelada', 'cancelado')}`,
      description: `Cuando el equipo o el cliente cancela y se libera el horario.`,
    },
    {
      value: 'appointment.expired',
      label: `${cleanSingular} ${adj('vencida', 'vencido')} sin confirmar`,
      description: `Cuando vence el tiempo límite (SLA) de una solicitud pendiente sin confirmación.`,
    },
    {
      value: 'appointment.no_show',
      label: `Inasistencia (${lowerSingular})`,
      description: `Cuando pasa el horario confirmado y queda marcada como no asistida.`,
    },
  ]
}

export const APPOINTMENT_WEBHOOK_EVENT_OPTIONS = getAppointmentWebhookEventOptions('Cita')

export function formatWebhookEventLabel(
  type: string | null | undefined,
  singularAppointmentLabel = 'Cita'
): string {
  if (!type) return '—'
  if (type === 'order.test') return 'Prueba de pedido'
  if (type === 'appointment.test') {
    return `Prueba de ${singularAppointmentLabel.trim().toLowerCase() || 'cita'}`
  }
  const orderOption = ORDER_WEBHOOK_EVENT_OPTIONS.find((item) => item.value === type)
  if (orderOption) return orderOption.label

  const appointmentOption = getAppointmentWebhookEventOptions(singularAppointmentLabel).find(
    (item) => item.value === type
  )
  if (appointmentOption) return appointmentOption.label

  return type
}

export const WEBHOOK_DELIVERY_STATUSES = ['pending', 'retrying', 'delivered', 'failed'] as const
export type WebhookDeliveryStatus = (typeof WEBHOOK_DELIVERY_STATUSES)[number]

export interface WebhookEndpoint {
  id: string
  tenantId: string
  url: string
  description: string | null
  events: string[]
  enabled: boolean
  includeAppointmentNotes?: boolean
  consecutiveFailures: number
  disabledReason: string | null
  lastDeliveryAt: string | null
  createdAt: string
  updatedAt: string
}

export interface WebhookEndpointCreated {
  endpoint: WebhookEndpoint
  secret: string
}

export interface WebhookTestResult {
  ok: boolean
  statusCode: number | null
  durationMs: number
  responseSnippet: string | null
  error: string | null
}

export interface WebhookDelivery {
  id: string
  endpointId: string
  eventId: string
  eventType: string | null
  status: WebhookDeliveryStatus
  attempts: number
  lastStatusCode: number | null
  lastError: string | null
  lastResponseSnippet: string | null
  lastDurationMs: number | null
  nextAttemptAt: string | null
  deliveredAt: string | null
  createdAt: string
}

export interface WebhookDeliveryPage {
  data: WebhookDelivery[]
  nextCursor: string | null
}

export interface WebhookEndpointInput {
  url: string
  description?: string | null
  events: SubscribableWebhookEventName[]
  enabled?: boolean
  includeAppointmentNotes?: boolean
}

function normalizeEndpoint(value: any): WebhookEndpoint {
  return {
    id: String(value?.id ?? ''),
    tenantId: String(value?.tenantId ?? ''),
    url: String(value?.url ?? ''),
    description: value?.description ?? null,
    events: Array.isArray(value?.events) ? value.events.map(String) : [],
    enabled: Boolean(value?.enabled),
    includeAppointmentNotes: Boolean(value?.includeAppointmentNotes),
    consecutiveFailures: Number(value?.consecutiveFailures ?? 0),
    disabledReason: value?.disabledReason ?? null,
    lastDeliveryAt: value?.lastDeliveryAt ?? null,
    createdAt: String(value?.createdAt ?? ''),
    updatedAt: String(value?.updatedAt ?? ''),
  }
}

function normalizeDelivery(value: any): WebhookDelivery {
  const status = WEBHOOK_DELIVERY_STATUSES.includes(value?.status)
    ? (value.status as WebhookDeliveryStatus)
    : 'pending'
  return {
    id: String(value?.id ?? ''),
    endpointId: String(value?.endpointId ?? ''),
    eventId: String(value?.eventId ?? ''),
    eventType: value?.eventType ?? null,
    status,
    attempts: Number(value?.attempts ?? 0),
    lastStatusCode: value?.lastStatusCode ?? null,
    lastError: value?.lastError ?? null,
    lastResponseSnippet: value?.lastResponseSnippet ?? null,
    lastDurationMs: value?.lastDurationMs ?? null,
    nextAttemptAt: value?.nextAttemptAt ?? null,
    deliveredAt: value?.deliveredAt ?? null,
    createdAt: String(value?.createdAt ?? ''),
  }
}

export const orderWebhooksApi = {
  async listEndpoints(): Promise<WebhookEndpoint[]> {
    const response = await api.get('/api/webhooks/endpoints')
    const data = Array.isArray(response.data?.data) ? response.data.data : []
    return data.map(normalizeEndpoint)
  },

  async createEndpoint(input: WebhookEndpointInput): Promise<WebhookEndpointCreated> {
    const response = await api.post('/api/webhooks/endpoints', input)
    return {
      endpoint: normalizeEndpoint(response.data?.data?.endpoint),
      secret: String(response.data?.data?.secret ?? ''),
    }
  },

  async updateEndpoint(id: string, input: Partial<WebhookEndpointInput>): Promise<WebhookEndpoint> {
    const response = await api.patch(`/api/webhooks/endpoints/${id}`, input)
    return normalizeEndpoint(response.data?.data)
  },

  async deleteEndpoint(id: string): Promise<void> {
    await api.delete(`/api/webhooks/endpoints/${id}`)
  },

  async rotateSecret(id: string): Promise<WebhookEndpointCreated> {
    const response = await api.post(`/api/webhooks/endpoints/${id}/rotate-secret`)
    return {
      endpoint: normalizeEndpoint(response.data?.data?.endpoint),
      secret: String(response.data?.data?.secret ?? ''),
    }
  },

  async testEndpoint(id: string): Promise<WebhookTestResult> {
    const response = await api.post(`/api/webhooks/endpoints/${id}/test`)
    const data = response.data?.data ?? {}
    return {
      ok: Boolean(data.ok),
      statusCode: data.statusCode ?? null,
      durationMs: Number(data.durationMs ?? 0),
      responseSnippet: data.responseSnippet ?? null,
      error: data.error ?? null,
    }
  },

  async listDeliveries(
    endpointId: string,
    options: { status?: WebhookDeliveryStatus; cursor?: string; limit?: number } = {}
  ): Promise<WebhookDeliveryPage> {
    const params = new URLSearchParams()
    if (options.status) params.set('status', options.status)
    if (options.cursor) params.set('cursor', options.cursor)
    if (options.limit) params.set('limit', String(options.limit))
    const query = params.toString()

    const response = await api.get(
      `/api/webhooks/endpoints/${endpointId}/deliveries${query ? `?${query}` : ''}`
    )
    const data = Array.isArray(response.data?.data) ? response.data.data : []
    return {
      data: data.map(normalizeDelivery),
      nextCursor: response.data?.nextCursor ?? null,
    }
  },

  async redeliver(deliveryId: string): Promise<WebhookDelivery> {
    const response = await api.post(`/api/webhooks/deliveries/${deliveryId}/redeliver`)
    return normalizeDelivery(response.data?.data)
  },
}
